import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Calculator, DollarSign, Users, 
  IndianRupee, Save, RefreshCw, FileText, Percent,
  TrendingUp, TrendingDown, Briefcase, Clock, CalendarDays,
  Wallet
} from 'lucide-react';
import PayDistributionTab from './PayDistributionTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCY_OPTIONS = [
  { value: 'monthly', label: 'Monthly', multiplier: 12 },
  { value: 'quarterly', label: 'Quarterly', multiplier: 4 },
  { value: 'half_yearly', label: 'Half Yearly', multiplier: 2 },
  { value: 'yearly', label: 'Yearly', multiplier: 1 }
];

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '₹0';
  const num = Number(value);
  if (isNaN(num)) return '₹0';
  return `₹${num.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const PayrollPortal = ({ user }) => {
  const [activeTab, setActiveTab] = useState('calculator');
  const [loading, setLoading] = useState(true);
  const [components, setComponents] = useState([]);
  const [templates, setTemplates] = useState([]);
  
  // Calculator states
  const [employeeType, setEmployeeType] = useState('full_time');
  const [contractualPeriod, setContractualPeriod] = useState(12);
  const [monthlySalary, setMonthlySalary] = useState('');
  const [calculatorComponents, setCalculatorComponents] = useState([]);
  const [calculationResult, setCalculationResult] = useState(null);
  
  // Dialog states
  const [showComponentDialog, setShowComponentDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  
  // Form states
  const [componentForm, setComponentForm] = useState({
    name: '',
    component_type: 'fixed',
    amount: '',
    percentage: '',
    frequency: 'monthly',
    is_deduction: false,
    remarks: ''
  });
  const [templateForm, setTemplateForm] = useState({
    name: '',
    employee_type: 'full_time',
    description: ''
  });

  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const fetchComponents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/payroll/components`, config);
      setComponents(response.data);
    } catch (error) {
      console.error('Error fetching components:', error);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/payroll/templates`, config);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, []);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchComponents(), fetchTemplates()]);
    setLoading(false);
  }, [fetchComponents, fetchTemplates]);

  useEffect(() => {
    fetchAllData();
  }, []);

  // Add a new component row to calculator
  const addComponentToCalculator = () => {
    setCalculatorComponents([
      ...calculatorComponents,
      {
        id: `temp_${Date.now()}`,
        name: '',
        component_type: 'fixed',
        amount: 0,
        percentage: 0,
        frequency: 'monthly',
        is_deduction: false,
        remarks: ''
      }
    ]);
  };

  // Update a component in calculator
  const updateCalculatorComponent = (index, field, value) => {
    const updated = [...calculatorComponents];
    updated[index] = { ...updated[index], [field]: value };
    
    // Clear amount/percentage when switching type
    if (field === 'component_type') {
      if (value === 'fixed') {
        updated[index].percentage = 0;
      } else {
        updated[index].amount = 0;
      }
    }
    
    setCalculatorComponents(updated);
  };

  // Remove a component from calculator
  const removeCalculatorComponent = (index) => {
    setCalculatorComponents(calculatorComponents.filter((_, i) => i !== index));
  };

  // Load a saved component into calculator
  const loadSavedComponent = (component) => {
    const exists = calculatorComponents.find(c => c.name === component.name);
    if (exists) {
      toast.error('This component is already added');
      return;
    }
    setCalculatorComponents([
      ...calculatorComponents,
      {
        id: component.id,
        name: component.name,
        component_type: component.component_type,
        amount: component.amount || 0,
        percentage: component.percentage || 0,
        frequency: component.frequency || 'monthly',
        is_deduction: component.is_deduction,
        remarks: component.remarks || ''
      }
    ]);
    toast.success(`Added ${component.name}`);
  };

  // Calculate salary
  const calculateSalary = async () => {
    if (!monthlySalary || parseFloat(monthlySalary) <= 0) {
      toast.error('Please enter a valid monthly salary');
      return;
    }

    if (calculatorComponents.length === 0) {
      toast.error('Please add at least one salary component');
      return;
    }

    // Validate all components
    for (const comp of calculatorComponents) {
      if (!comp.name.trim()) {
        toast.error('All components must have a name');
        return;
      }
      if (comp.component_type === 'fixed' && (!comp.amount || comp.amount <= 0)) {
        toast.error(`Fixed component "${comp.name}" must have an amount`);
        return;
      }
      if (comp.component_type === 'variable' && (!comp.percentage || comp.percentage <= 0)) {
        toast.error(`Variable component "${comp.name}" must have a percentage`);
        return;
      }
    }

    try {
      const response = await axios.post(`${API}/payroll/calculate`, {
        employee_type: employeeType,
        contractual_period: employeeType === 'contractor' ? parseInt(contractualPeriod) : 12,
        monthly_salary: parseFloat(monthlySalary),
        components: calculatorComponents
      }, config);
      
      setCalculationResult(response.data);
      toast.success('Salary calculated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Calculation failed');
    }
  };

  // Reset calculator
  const resetCalculator = () => {
    setEmployeeType('full_time');
    setContractualPeriod(12);
    setMonthlySalary('');
    setCalculatorComponents([]);
    setCalculationResult(null);
  };

  // Save component to database
  const handleSaveComponent = async (e) => {
    e.preventDefault();
    
    // Validate
    if (componentForm.component_type === 'fixed' && !componentForm.amount) {
      toast.error('Fixed components must have an amount');
      return;
    }
    if (componentForm.component_type === 'variable' && !componentForm.percentage) {
      toast.error('Variable components must have a percentage');
      return;
    }
    
    try {
      const payload = {
        name: componentForm.name,
        component_type: componentForm.component_type,
        frequency: componentForm.frequency,
        is_deduction: componentForm.is_deduction,
        remarks: componentForm.remarks,
        amount: componentForm.component_type === 'fixed' ? parseFloat(componentForm.amount) : null,
        percentage: componentForm.component_type === 'variable' ? parseFloat(componentForm.percentage) : null
      };
      
      if (editingComponent) {
        await axios.put(`${API}/payroll/components/${editingComponent.id}`, payload, config);
        toast.success('Component updated successfully');
      } else {
        await axios.post(`${API}/payroll/components`, payload, config);
        toast.success('Component created successfully');
      }
      setShowComponentDialog(false);
      setEditingComponent(null);
      setComponentForm({ name: '', component_type: 'fixed', amount: '', percentage: '', frequency: 'monthly', is_deduction: false, remarks: '' });
      fetchComponents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save component');
    }
  };

  // Delete component
  const handleDeleteComponent = async (componentId) => {
    if (!window.confirm('Are you sure you want to delete this component?')) return;
    try {
      await axios.delete(`${API}/payroll/components/${componentId}`, config);
      toast.success('Component deleted');
      fetchComponents();
    } catch (error) {
      toast.error('Failed to delete component');
    }
  };

  // Edit component
  const handleEditComponent = (component) => {
    setEditingComponent(component);
    setComponentForm({
      name: component.name,
      component_type: component.component_type,
      amount: component.amount?.toString() || '',
      percentage: component.percentage?.toString() || '',
      frequency: component.frequency || 'monthly',
      is_deduction: component.is_deduction,
      remarks: component.remarks || ''
    });
    setShowComponentDialog(true);
  };

  // Save template
  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    if (calculatorComponents.length === 0) {
      toast.error('Please add components to save as template');
      return;
    }

    try {
      await axios.post(`${API}/payroll/templates`, {
        name: templateForm.name,
        employee_type: employeeType,
        description: templateForm.description,
        components: calculatorComponents
      }, config);
      toast.success('Template saved successfully');
      setShowTemplateDialog(false);
      setTemplateForm({ name: '', employee_type: 'full_time', description: '' });
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  // Load template
  const loadTemplate = (template) => {
    setEmployeeType(template.employee_type);
    setCalculatorComponents(template.components.map(c => ({
      ...c,
      id: c.id || `temp_${Date.now()}_${Math.random()}`
    })));
    toast.success(`Loaded template: ${template.name}`);
  };

  // Delete template
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await axios.delete(`${API}/payroll/templates/${templateId}`, config);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payroll-portal">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <IndianRupee className="w-6 h-6 text-green-600" />
            Payroll Portal
          </h2>
          <p className="text-gray-600">Salary Calculator & Payroll Management</p>
        </div>
        
        <Button variant="outline" onClick={fetchAllData} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-[600px]">
          <TabsTrigger value="calculator" data-testid="tab-calculator">
            <Calculator className="w-4 h-4 mr-2" />
            Salary Calculator
          </TabsTrigger>
          <TabsTrigger value="components" data-testid="tab-components">
            <FileText className="w-4 h-4 mr-2" />
            Components
          </TabsTrigger>
          <TabsTrigger value="pay-distribution" data-testid="tab-pay-distribution">
            <Wallet className="w-4 h-4 mr-2" />
            Pay Distribution
          </TabsTrigger>
        </TabsList>

        {/* Calculator Tab */}
        <TabsContent value="calculator" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Panel - Input */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Info Card */}
              <Card data-testid="calculator-input">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Employee Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Employee Type *</Label>
                      <Select value={employeeType} onValueChange={setEmployeeType}>
                        <SelectTrigger data-testid="employee-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full_time">
                            <div className="flex items-center gap-2">
                              <Briefcase className="w-4 h-4" />
                              Full Time
                            </div>
                          </SelectItem>
                          <SelectItem value="contractor">
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              Contractor
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {employeeType === 'contractor' && (
                      <div>
                        <Label>Contractual Period (Months) *</Label>
                        <Input
                          type="number"
                          min="1"
                          max="60"
                          value={contractualPeriod}
                          onChange={(e) => setContractualPeriod(e.target.value)}
                          placeholder="Enter months"
                          data-testid="contractual-period-input"
                        />
                      </div>
                    )}
                    
                    <div>
                      <Label>Monthly Base Salary (₹) *</Label>
                      <Input
                        type="number"
                        min="0"
                        value={monthlySalary}
                        onChange={(e) => setMonthlySalary(e.target.value)}
                        placeholder="Enter monthly salary"
                        data-testid="monthly-salary-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Salary Components Card */}
              <Card data-testid="salary-components">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Percent className="w-5 h-5" />
                      Salary Components
                    </CardTitle>
                    <CardDescription>
                      Fixed: Enter amount directly | Variable: Enter % of (Base - Fixed)
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {components.length > 0 && (
                      <Select onValueChange={(id) => {
                        const comp = components.find(c => c.id === id);
                        if (comp) loadSavedComponent(comp);
                      }}>
                        <SelectTrigger className="w-[180px]" data-testid="load-component-select">
                          <SelectValue placeholder="Load Saved" />
                        </SelectTrigger>
                        <SelectContent>
                          {components.map(comp => (
                            <SelectItem key={comp.id} value={comp.id}>
                              {comp.name} ({comp.component_type === 'fixed' ? formatCurrency(comp.amount) : `${comp.percentage}%`})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Button onClick={addComponentToCalculator} data-testid="add-component-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Row
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {calculatorComponents.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Percent className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No components added yet. Click "Add Row" or "Load Saved" to add components.</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[160px]">Component Name</TableHead>
                            <TableHead className="w-[100px]">Type</TableHead>
                            <TableHead className="w-[120px]">Amount / %</TableHead>
                            <TableHead className="w-[120px]">Frequency</TableHead>
                            <TableHead className="w-[80px]">Deduction?</TableHead>
                            <TableHead>Remarks</TableHead>
                            <TableHead className="w-[60px]">Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {calculatorComponents.map((comp, index) => (
                            <TableRow key={comp.id || index}>
                              <TableCell>
                                <Input
                                  value={comp.name}
                                  onChange={(e) => updateCalculatorComponent(index, 'name', e.target.value)}
                                  placeholder="Component name"
                                  data-testid={`comp-name-${index}`}
                                />
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={comp.component_type}
                                  onValueChange={(v) => updateCalculatorComponent(index, 'component_type', v)}
                                >
                                  <SelectTrigger data-testid={`comp-type-${index}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="fixed">Fixed</SelectItem>
                                    <SelectItem value="variable">Variable</SelectItem>
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                {comp.component_type === 'fixed' ? (
                                  <div className="relative">
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                                    <Input
                                      type="number"
                                      min="0"
                                      value={comp.amount || ''}
                                      onChange={(e) => updateCalculatorComponent(index, 'amount', parseFloat(e.target.value) || 0)}
                                      placeholder="Amount"
                                      className="pl-6"
                                      data-testid={`comp-amount-${index}`}
                                    />
                                  </div>
                                ) : (
                                  <div className="relative">
                                    <Input
                                      type="number"
                                      min="0"
                                      max="100"
                                      value={comp.percentage || ''}
                                      onChange={(e) => updateCalculatorComponent(index, 'percentage', parseFloat(e.target.value) || 0)}
                                      placeholder="%"
                                      className="pr-6"
                                      data-testid={`comp-percent-${index}`}
                                    />
                                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                <Select
                                  value={comp.frequency || 'monthly'}
                                  onValueChange={(v) => updateCalculatorComponent(index, 'frequency', v)}
                                >
                                  <SelectTrigger data-testid={`comp-freq-${index}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {FREQUENCY_OPTIONS.map(opt => (
                                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                <Switch
                                  checked={comp.is_deduction}
                                  onCheckedChange={(v) => updateCalculatorComponent(index, 'is_deduction', v)}
                                  data-testid={`comp-deduction-${index}`}
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={comp.remarks}
                                  onChange={(e) => updateCalculatorComponent(index, 'remarks', e.target.value)}
                                  placeholder="Remarks"
                                  data-testid={`comp-remarks-${index}`}
                                />
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeCalculatorComponent(index)}
                                  data-testid={`remove-comp-${index}`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex justify-between mt-4">
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={resetCalculator} data-testid="reset-btn">
                        Reset
                      </Button>
                      {calculatorComponents.length > 0 && (
                        <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
                          <DialogTrigger asChild>
                            <Button variant="outline" data-testid="save-template-btn">
                              <Save className="w-4 h-4 mr-2" />
                              Save as Template
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Save as Template</DialogTitle>
                              <DialogDescription>Save current components as a reusable template</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div>
                                <Label>Template Name *</Label>
                                <Input
                                  value={templateForm.name}
                                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                                  placeholder="e.g., Standard Full-Time Package"
                                />
                              </div>
                              <div>
                                <Label>Description</Label>
                                <Textarea
                                  value={templateForm.description}
                                  onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                                  placeholder="Brief description"
                                />
                              </div>
                              <Button onClick={handleSaveTemplate} className="w-full">
                                Save Template
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      )}
                    </div>
                    <Button onClick={calculateSalary} className="bg-green-600 hover:bg-green-700" data-testid="calculate-btn">
                      <Calculator className="w-4 h-4 mr-2" />
                      Calculate Salary
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Calculation Result */}
              {calculationResult && (
                <Card className="border-green-200 bg-green-50" data-testid="calculation-result">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-green-700">
                      <IndianRupee className="w-5 h-5" />
                      Salary Breakdown
                    </CardTitle>
                    <CardDescription>
                      {calculationResult.employee_type === 'full_time' ? 'Annual Calculation (12 months)' : `Contractual Period: ${calculationResult.contractual_period} months`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Base Salary Info */}
                    <div className="mb-4 p-4 bg-white rounded-lg border grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-gray-600">Base Monthly Salary</p>
                        <p className="text-lg font-bold text-gray-800">{formatCurrency(calculationResult.base_monthly_salary)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total Fixed (Monthly)</p>
                        <p className="text-lg font-bold text-blue-600">{formatCurrency(calculationResult.total_fixed_monthly)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Variable Base</p>
                        <p className="text-lg font-bold text-purple-600">{formatCurrency(calculationResult.variable_base)}</p>
                        <p className="text-xs text-gray-500">(Base - Fixed)</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Base {calculationResult.employee_type === 'full_time' ? 'Annual' : 'Total'}</p>
                        <p className="text-lg font-bold text-gray-800">{formatCurrency(calculationResult.base_total_salary)}</p>
                      </div>
                    </div>

                    {/* Components Breakdown */}
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Component</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Frequency</TableHead>
                          <TableHead className="text-right">Amount / %</TableHead>
                          <TableHead className="text-right">Per Occurrence</TableHead>
                          <TableHead className="text-right">Monthly Equiv.</TableHead>
                          <TableHead className="text-right">Annual / Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {calculationResult.components.map((comp, idx) => (
                          <TableRow key={idx} className={comp.is_deduction ? 'bg-red-50' : 'bg-green-50'}>
                            <TableCell className="font-medium">
                              {comp.name}
                              {comp.is_deduction && <Badge variant="destructive" className="ml-2 text-xs">Deduction</Badge>}
                            </TableCell>
                            <TableCell>
                              <Badge variant={comp.component_type === 'fixed' ? 'default' : 'secondary'}>
                                {comp.component_type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="flex items-center gap-1 w-fit">
                                <CalendarDays className="w-3 h-3" />
                                {comp.frequency_label}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              {comp.component_type === 'fixed' ? formatCurrency(comp.amount) : `${comp.percentage}%`}
                            </TableCell>
                            <TableCell className={`text-right ${comp.is_deduction ? 'text-red-600' : 'text-green-600'}`}>
                              {comp.is_deduction ? '-' : '+'}{formatCurrency(comp.per_occurrence_amount)}
                            </TableCell>
                            <TableCell className={`text-right ${comp.is_deduction ? 'text-red-600' : 'text-green-600'}`}>
                              {comp.is_deduction ? '-' : '+'}{formatCurrency(comp.monthly_equivalent)}
                            </TableCell>
                            <TableCell className={`text-right font-medium ${comp.is_deduction ? 'text-red-600' : 'text-green-600'}`}>
                              {comp.is_deduction ? '-' : '+'}{formatCurrency(comp.total_amount)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {/* Summary */}
                    <div className="mt-4 space-y-2 p-4 bg-white rounded-lg border">
                      <div className="flex justify-between text-green-600">
                        <span>Total Additions (Annual):</span>
                        <span>+{formatCurrency(calculationResult.summary.total_additions_annual)}</span>
                      </div>
                      <div className="flex justify-between text-red-600">
                        <span>Total Deductions (Annual):</span>
                        <span>-{formatCurrency(calculationResult.summary.total_deductions_annual)}</span>
                      </div>
                      <hr />
                      <div className="flex justify-between font-bold text-lg">
                        <span>Net Salary (Monthly):</span>
                        <span className="text-blue-700">{formatCurrency(calculationResult.summary.net_monthly)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-lg">
                        <span>Net Salary (Annual):</span>
                        <span className="text-blue-700">{formatCurrency(calculationResult.summary.net_annual)}</span>
                      </div>
                      {calculationResult.employee_type === 'contractor' && (
                        <div className="flex justify-between font-bold text-xl text-green-700 pt-2 border-t">
                          <span>Net Salary (Contract Total - {calculationResult.contractual_period} months):</span>
                          <span>{formatCurrency(calculationResult.summary.net_total)}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Panel - Templates & Info */}
            <div className="space-y-6">
              <Card data-testid="templates-panel">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Saved Templates
                  </CardTitle>
                  <CardDescription>Quick load salary structures</CardDescription>
                </CardHeader>
                <CardContent>
                  {templates.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No templates saved yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {templates.map(template => (
                        <div 
                          key={template.id} 
                          className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                          data-testid={`template-${template.id}`}
                        >
                          <div className="flex justify-between items-start">
                            <div onClick={() => loadTemplate(template)} className="flex-1">
                              <p className="font-medium">{template.name}</p>
                              <p className="text-xs text-gray-500">
                                {template.employee_type === 'full_time' ? 'Full Time' : 'Contractor'} | {template.components?.length || 0} components
                              </p>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(template.id); }}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Info Card */}
              <Card className="bg-blue-50 border-blue-200">
                <CardHeader>
                  <CardTitle className="text-blue-700 text-sm">How Calculator Works</CardTitle>
                </CardHeader>
                <CardContent className="text-sm text-blue-600 space-y-2">
                  <p><strong>Fixed Components:</strong></p>
                  <p className="ml-2">• Enter exact amount (₹)</p>
                  <p className="ml-2">• Paid per frequency selected</p>
                  <p className="mt-2"><strong>Variable Components:</strong></p>
                  <p className="ml-2">• Enter % of Variable Base</p>
                  <p className="ml-2">• Variable Base = Monthly Salary - Fixed Additions</p>
                  <p className="mt-2"><strong>Frequency:</strong></p>
                  <p className="ml-2">• Monthly (×12), Quarterly (×4)</p>
                  <p className="ml-2">• Half Yearly (×2), Yearly (×1)</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Components Tab */}
        <TabsContent value="components" className="space-y-6">
          <Card data-testid="manage-components">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Percent className="w-5 h-5" />
                  Manage Salary Components
                </CardTitle>
                <CardDescription>Create reusable salary components with fixed amounts or variable percentages</CardDescription>
              </div>
              <Dialog open={showComponentDialog} onOpenChange={(open) => {
                setShowComponentDialog(open);
                if (!open) {
                  setEditingComponent(null);
                  setComponentForm({ name: '', component_type: 'fixed', amount: '', percentage: '', frequency: 'monthly', is_deduction: false, remarks: '' });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-new-component-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Component
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingComponent ? 'Edit Component' : 'Create New Component'}</DialogTitle>
                    <DialogDescription>
                      {editingComponent ? 'Update the component details' : 'Add a new reusable salary component'}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSaveComponent} className="space-y-4">
                    <div>
                      <Label>Component Name *</Label>
                      <Input
                        value={componentForm.name}
                        onChange={(e) => setComponentForm({ ...componentForm, name: e.target.value })}
                        required
                        placeholder="e.g., HRA, PF, Bonus"
                        data-testid="component-name-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Type *</Label>
                        <Select
                          value={componentForm.component_type}
                          onValueChange={(v) => setComponentForm({ ...componentForm, component_type: v, amount: '', percentage: '' })}
                        >
                          <SelectTrigger data-testid="component-type-input">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fixed">Fixed (Amount)</SelectItem>
                            <SelectItem value="variable">Variable (%)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Frequency *</Label>
                        <Select
                          value={componentForm.frequency}
                          onValueChange={(v) => setComponentForm({ ...componentForm, frequency: v })}
                        >
                          <SelectTrigger data-testid="component-frequency-input">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {FREQUENCY_OPTIONS.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    {componentForm.component_type === 'fixed' ? (
                      <div>
                        <Label>Amount (₹) *</Label>
                        <Input
                          type="number"
                          min="0"
                          value={componentForm.amount}
                          onChange={(e) => setComponentForm({ ...componentForm, amount: e.target.value })}
                          required
                          placeholder="Enter fixed amount"
                          data-testid="component-amount-input"
                        />
                      </div>
                    ) : (
                      <div>
                        <Label>Percentage (%) *</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={componentForm.percentage}
                          onChange={(e) => setComponentForm({ ...componentForm, percentage: e.target.value })}
                          required
                          placeholder="Enter percentage"
                          data-testid="component-percentage-input"
                        />
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={componentForm.is_deduction}
                        onCheckedChange={(v) => setComponentForm({ ...componentForm, is_deduction: v })}
                        data-testid="component-deduction-input"
                      />
                      <Label>Is Deduction (e.g., PF, Tax, TDS)</Label>
                    </div>
                    <div>
                      <Label>Remarks</Label>
                      <Textarea
                        value={componentForm.remarks}
                        onChange={(e) => setComponentForm({ ...componentForm, remarks: e.target.value })}
                        placeholder="Additional notes"
                        data-testid="component-remarks-input"
                      />
                    </div>
                    <Button type="submit" className="w-full" data-testid="save-component-btn">
                      {editingComponent ? 'Update Component' : 'Create Component'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {components.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Percent className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No components created yet. Click "Add Component" to create one.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Additions */}
                  <div>
                    <h3 className="text-lg font-semibold text-green-700 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Additions ({components.filter(c => !c.is_deduction).length})
                    </h3>
                    <div className="space-y-2">
                      {components.filter(c => !c.is_deduction).map(comp => (
                        <div 
                          key={comp.id} 
                          className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                          data-testid={`component-${comp.id}`}
                        >
                          <div>
                            <p className="font-medium text-green-800">{comp.name}</p>
                            <div className="flex gap-2 text-xs text-green-600 flex-wrap">
                              <Badge variant="outline">{comp.component_type}</Badge>
                              <Badge variant="outline">{(comp.frequency || 'monthly').replace('_', ' ')}</Badge>
                              <span>
                                {comp.component_type === 'fixed' ? formatCurrency(comp.amount) : `${comp.percentage}%`}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEditComponent(comp)}>
                              <Edit className="w-4 h-4 text-green-700" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteComponent(comp.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {components.filter(c => !c.is_deduction).length === 0 && (
                        <p className="text-gray-500 text-sm">No addition components</p>
                      )}
                    </div>
                  </div>

                  {/* Deductions */}
                  <div>
                    <h3 className="text-lg font-semibold text-red-700 mb-3 flex items-center gap-2">
                      <TrendingDown className="w-5 h-5" />
                      Deductions ({components.filter(c => c.is_deduction).length})
                    </h3>
                    <div className="space-y-2">
                      {components.filter(c => c.is_deduction).map(comp => (
                        <div 
                          key={comp.id} 
                          className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg"
                          data-testid={`component-${comp.id}`}
                        >
                          <div>
                            <p className="font-medium text-red-800">{comp.name}</p>
                            <div className="flex gap-2 text-xs text-red-600 flex-wrap">
                              <Badge variant="outline">{comp.component_type}</Badge>
                              <Badge variant="outline">{(comp.frequency || 'monthly').replace('_', ' ')}</Badge>
                              <span>
                                {comp.component_type === 'fixed' ? formatCurrency(comp.amount) : `${comp.percentage}%`}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEditComponent(comp)}>
                              <Edit className="w-4 h-4 text-red-700" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteComponent(comp.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {components.filter(c => c.is_deduction).length === 0 && (
                        <p className="text-gray-500 text-sm">No deduction components</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pay Distribution Tab */}
        <TabsContent value="pay-distribution" className="space-y-6">
          <PayDistributionTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PayrollPortal;
